// =============================================================================
// Module Project - Repository: Permission (New Hierarchical Model)
// Handles project_permissions and space_members for the new permission system
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

export type PermissionLevel = 'full' | 'edit' | 'comment' | 'view';

export interface ProjectMemberInfo {
  org_member_id: string;
  user_id: string;
  effective_permission: PermissionLevel;
  source: 'project' | 'space' | 'workspace';
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  has_space_access: boolean; // True if member also has space_members entry (for rollback UX)
}

export interface SpaceMemberInfo {
  org_member_id: string;
  user_id: string;
  permission: PermissionLevel;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

export interface OrgMemberInfo {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

// Source types: 'project' = direct override, 'space' = inherited, 'workspace' = public project default, 'org' = implicit admin bypass
export type PermissionSource = 'project' | 'space' | 'workspace' | 'org';

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PROJECT MEMBERS (using new RPC)
// Returns members with explicit access (direct project override OR space inheritance)
// Owner/Admin have implicit access but are NOT listed here
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchProjectMembers(projectId: string): Promise<ProjectMemberInfo[]> {
  // Try RPC first (uses view + security checks)
  const { data, error } = await supabase.rpc('get_project_members', {
    _project_id: projectId,
  });

  if (!error && data) {
    return (data || []).map((m: any) => ({
      org_member_id: m.org_member_id,
      user_id: m.user_id,
      effective_permission: m.effective_permission as PermissionLevel,
      source: m.source as 'project' | 'space' | 'workspace',
      full_name: m.full_name,
      email: m.email,
      avatar_url: m.avatar_url,
      has_space_access: m.has_space_access ?? false,
    }));
  }

  // Fallback: direct query on project_permissions + space inheritance
  console.warn('get_project_members RPC failed, using fallback:', error?.message);
  return fetchProjectMembersFallback(projectId);
}

/**
 * Fallback: query project_permissions + space_members directly when RPC is unavailable.
 * Mirrors the logic of the project_effective_members view.
 */
async function fetchProjectMembersFallback(projectId: string): Promise<ProjectMemberInfo[]> {
  const result: ProjectMemberInfo[] = [];
  const seenMemberIds = new Set<string>();

  // 1. Direct project permissions
  const { data: directPerms } = await supabase
    .from('project_permissions')
    .select('org_member_id, permission')
    .eq('project_id', projectId);

  const directMemberIds = (directPerms || []).map(p => p.org_member_id);

  if (directPerms && directPerms.length > 0) {
    // Fetch member details
    const { data: members } = await supabase
      .from('core_organization_members')
      .select('id, user_id, status')
      .in('id', directMemberIds)
      .eq('status', 'active');

    if (members && members.length > 0) {
      const userIds = members.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('core_profiles')
        .select('id, full_name, email, avatar_url')
        .in('id', userIds);

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      for (const perm of directPerms) {
        const member = members.find(m => m.id === perm.org_member_id);
        if (!member) continue;
        const profile = profileMap.get(member.user_id);
        seenMemberIds.add(perm.org_member_id);
        result.push({
          org_member_id: perm.org_member_id,
          user_id: member.user_id,
          effective_permission: perm.permission as PermissionLevel,
          source: 'project',
          full_name: profile?.full_name || null,
          email: profile?.email || '',
          avatar_url: profile?.avatar_url || null,
          has_space_access: false, // will check below
        });
      }
    }
  }

  // 2. Space inherited members (only for non-private projects)
  const { data: project } = await supabase
    .from('project_projects')
    .select('space_id, visibility')
    .eq('id', projectId)
    .single();

  if (project?.space_id && project.visibility !== 'private') {
    const { data: spaceMembers, error: spaceMembersError } = await supabase
      .from('space_members')
      .select('org_member_id, permission')
      .eq('space_id', project.space_id);

    // Graceful fallback when space_members table doesn't exist in external DBs
    if (spaceMembersError && (spaceMembersError.code === 'PGRST205' || spaceMembersError.code === '42P01')) {
      console.warn('[permissionRepository] space_members table not found, skipping space inheritance');
    } else if (spaceMembers && spaceMembers.length > 0) {
      const spaceMemberIds = spaceMembers
        .filter(sm => !seenMemberIds.has(sm.org_member_id))
        .map(sm => sm.org_member_id);

      // Mark direct members who also have space access
      for (const sm of spaceMembers) {
        if (seenMemberIds.has(sm.org_member_id)) {
          const existing = result.find(r => r.org_member_id === sm.org_member_id);
          if (existing) existing.has_space_access = true;
        }
      }

      if (spaceMemberIds.length > 0) {
        const { data: members } = await supabase
          .from('core_organization_members')
          .select('id, user_id, status')
          .in('id', spaceMemberIds)
          .eq('status', 'active');

        if (members && members.length > 0) {
          const userIds = members.map(m => m.user_id);
          const { data: profiles } = await supabase
            .from('core_profiles')
            .select('id, full_name, email, avatar_url')
            .in('id', userIds);

          const profileMap = new Map((profiles || []).map(p => [p.id, p]));

          for (const sm of spaceMembers) {
            if (seenMemberIds.has(sm.org_member_id)) continue;
            const member = members.find(m => m.id === sm.org_member_id);
            if (!member) continue;
            const profile = profileMap.get(member.user_id);
            seenMemberIds.add(sm.org_member_id);
            result.push({
              org_member_id: sm.org_member_id,
              user_id: member.user_id,
              effective_permission: sm.permission as PermissionLevel,
              source: 'space',
              full_name: profile?.full_name || null,
              email: profile?.email || '',
              avatar_url: profile?.avatar_url || null,
              has_space_access: true,
            });
          }
        }
      }
    }
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH SPACE MEMBERS (using new RPC)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchSpaceMembers(spaceId: string): Promise<SpaceMemberInfo[]> {
  const { data, error } = await supabase.rpc('get_space_members', {
    _space_id: spaceId,
  });

  if (error) {
    console.error('Error fetching space members:', error);
    throw error;
  }

  return (data || []).map((m: any) => ({
    org_member_id: m.org_member_id,
    user_id: m.user_id,
    permission: m.permission as PermissionLevel,
    full_name: m.full_name,
    email: m.email,
    avatar_url: m.avatar_url,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ORGANIZATION MEMBERS (for adding to project/space)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchOrganizationMembers(orgId: string): Promise<OrgMemberInfo[]> {
  const { data: membersData, error } = await supabase
    .from('core_organization_members')
    .select('id, user_id')
    .eq('org_id', orgId)
    .eq('status', 'active');

  if (error) throw error;
  if (!membersData || membersData.length === 0) return [];

  const userIds = membersData.map((m) => m.user_id);
  const { data: profilesData } = await supabase
    .from('core_profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', userIds);

  const profileMap = new Map((profilesData || []).map((p) => [p.id, p]));

  return membersData.map((m) => {
    const profile = profileMap.get(m.user_id);
    return {
      id: m.id,
      user_id: m.user_id,
      full_name: profile?.full_name || null,
      email: profile?.email || '',
      avatar_url: profile?.avatar_url || null,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD PROJECT PERMISSION (via RPC - replaces edge function)
// ─────────────────────────────────────────────────────────────────────────────
export async function addProjectPermission(
  projectId: string,
  orgMemberId: string,
  permission: PermissionLevel = 'view'
): Promise<void> {
  const { error } = await supabase.rpc('manage_project_permission_add', {
    _project_id: projectId,
    _org_member_id: orgMemberId,
    _permission: permission,
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE PROJECT PERMISSION (via RPC - replaces edge function)
// ─────────────────────────────────────────────────────────────────────────────
export async function updateProjectPermission(
  orgId: string,
  projectId: string,
  orgMemberId: string,
  permission: PermissionLevel
): Promise<void> {
  const { error } = await supabase.rpc('manage_project_permission_update', {
    _org_id: orgId,
    _project_id: projectId,
    _org_member_id: orgMemberId,
    _permission: permission,
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE PROJECT PERMISSION (via RPC - replaces edge function)
// ─────────────────────────────────────────────────────────────────────────────
export async function removeProjectPermission(
  orgId: string,
  projectId: string,
  orgMemberId: string
): Promise<void> {
  const { error } = await supabase.rpc('manage_project_permission_remove', {
    _org_id: orgId,
    _project_id: projectId,
    _org_member_id: orgMemberId,
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD SPACE MEMBER (direct insert - space_members has RLS)
// ─────────────────────────────────────────────────────────────────────────────
export async function addSpaceMember(
  spaceId: string,
  orgMemberId: string,
  permission: PermissionLevel = 'view'
): Promise<void> {
  const { error } = await supabase
    .from('space_members')
    .insert({
      space_id: spaceId,
      org_member_id: orgMemberId,
      permission,
    });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE SPACE MEMBER PERMISSION
// ─────────────────────────────────────────────────────────────────────────────
export async function updateSpaceMemberPermission(
  spaceId: string,
  orgMemberId: string,
  permission: PermissionLevel
): Promise<void> {
  const { error } = await supabase
    .from('space_members')
    .update({ permission })
    .eq('space_id', spaceId)
    .eq('org_member_id', orgMemberId);

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE SPACE MEMBER
// Returns { wasLastExplicitFull: boolean } to allow UI to show appropriate warning
// (Owner/Admin org have implicit full, so removal is always allowed)
// ─────────────────────────────────────────────────────────────────────────────
export interface RemoveSpaceMemberResult {
  wasLastExplicitFull: boolean;
}

export async function removeSpaceMember(
  spaceId: string,
  orgMemberId: string
): Promise<RemoveSpaceMemberResult> {
  let wasLastExplicitFull = false;

  // Check if this member has 'full' permission
  const { data: memberToRemove } = await supabase
    .from('space_members')
    .select('permission')
    .eq('space_id', spaceId)
    .eq('org_member_id', orgMemberId)
    .single();

  if (memberToRemove?.permission === 'full') {
    // Count remaining 'full' members (excluding this one)
    const { data: fullMembers } = await supabase
      .from('space_members')
      .select('org_member_id')
      .eq('space_id', spaceId)
      .eq('permission', 'full')
      .neq('org_member_id', orgMemberId);

    // Owner/Admin have implicit full, so we allow removal
    // But flag it for UI warning
    if (!fullMembers || fullMembers.length === 0) {
      wasLastExplicitFull = true;
    }
  }

  const { error } = await supabase
    .from('space_members')
    .delete()
    .eq('space_id', spaceId)
    .eq('org_member_id', orgMemberId);

  if (error) throw error;
  
  return { wasLastExplicitFull };
}

// ─────────────────────────────────────────────────────────────────────────────
// GET CURRENT USER'S EFFECTIVE PERMISSION ON A PROJECT
// Uses the DB function get_effective_permission
// ─────────────────────────────────────────────────────────────────────────────
export async function getCurrentUserPermission(
  projectId: string,
  userId: string
): Promise<PermissionLevel | null> {
  // Try RPC first
  const { data, error } = await supabase.rpc('get_effective_permission', {
    _user_id: userId,
    _project_id: projectId,
  });

  if (!error) {
    return data as PermissionLevel | null;
  }

  // Fallback: manual resolution (silencieux : non-critique, fallback couvre le cas)
  // On ne log pas pour éviter le bruit console (notamment en E2E lors du teardown).
  try {
    // Get project org_id and space_id
    const { data: project } = await supabase
      .from('project_projects')
      .select('org_id, space_id')
      .eq('id', projectId)
      .single();

    if (!project) return null;

    // Get member_id
    const { data: member } = await supabase
      .from('core_organization_members')
      .select('id')
      .eq('org_id', project.org_id)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();

    if (!member) return null;

    // Check if owner/admin (implicit full)
    const { data: roles } = await supabase
      .from('core_member_roles')
      .select('role:core_roles(code)')
      .eq('member_id', member.id);

    const roleCodes = (roles || []).map((r: any) => r.role?.code).filter(Boolean);
    if (roleCodes.includes('owner') || roleCodes.includes('admin')) {
      return 'full';
    }

    // Check direct project permission
    const { data: directPerm } = await supabase
      .from('project_permissions')
      .select('permission')
      .eq('project_id', projectId)
      .eq('org_member_id', member.id)
      .single();

    if (directPerm) return directPerm.permission as PermissionLevel;

    // Check space permission
    // Only inherit space permission if project is NOT private
    const { data: projVisibility } = await supabase
      .from('project_projects')
      .select('visibility')
      .eq('id', projectId)
      .single();

    if (project.space_id && projVisibility?.visibility !== 'private') {
      const { data: spacePerm, error: spaceErr } = await supabase
        .from('space_members')
        .select('permission')
        .eq('space_id', project.space_id)
        .eq('org_member_id', member.id)
        .single();

      if (!spaceErr && spacePerm) return spacePerm.permission as PermissionLevel;
    }

    // 4) Visibility-based default: members get 'edit' on public projects in public spaces
    if (projVisibility?.visibility === 'public') {
      const isMember = roleCodes.some((r: string) => ['member', 'manager'].includes(r));
      if (isMember) {
        let spaceVis = 'public';
        if (project.space_id) {
          const { data: spaceData } = await supabase
            .from('project_spaces')
            .select('visibility')
            .eq('id', project.space_id)
            .single();
          spaceVis = spaceData?.visibility || 'public';
        }
        if (spaceVis === 'public') {
          return 'edit';
        }
      }
    }

    // 5) No access
    return null;
  } catch (fallbackErr) {
    console.error('Fallback getCurrentUserPermission failed:', fallbackErr);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ORG ADMIN CHECK
// Quick check if a user has owner/admin role in the org
// ─────────────────────────────────────────────────────────────────────────────
export async function isOrgAdmin(orgId: string, userId: string): Promise<boolean> {
  try {
    const { data: member } = await supabase
      .from('core_organization_members')
      .select('id')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();

    if (!member) return false;

    const { data: roles } = await supabase
      .from('core_member_roles')
      .select('role:core_roles!inner(code)')
      .eq('member_id', member.id);

    return (roles || []).some((r: any) => 
      r.role?.code === 'owner' || r.role?.code === 'admin'
    );
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION HELPERS
// Map permission levels to capabilities
// ─────────────────────────────────────────────────────────────────────────────
export const PERMISSION_CAPABILITIES = {
  full: {
    canRead: true,
    canComment: true,
    canEdit: true,
    canManageMembers: true,
    canDelete: true,
    canManageSettings: true,
  },
  edit: {
    canRead: true,
    canComment: true,
    canEdit: true,
    canManageMembers: false,
    canDelete: false,
    canManageSettings: false,
  },
  comment: {
    canRead: true,
    canComment: true,
    canEdit: false,
    canManageMembers: false,
    canDelete: false,
    canManageSettings: false,
  },
  view: {
    canRead: true,
    canComment: false,
    canEdit: false,
    canManageMembers: false,
    canDelete: false,
    canManageSettings: false,
  },
} as const;

export function getCapabilities(permission: PermissionLevel | null) {
  if (!permission) {
    return {
      canRead: false,
      canComment: false,
      canEdit: false,
      canManageMembers: false,
      canDelete: false,
      canManageSettings: false,
    };
  }
  return PERMISSION_CAPABILITIES[permission];
}

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION LABELS (French)
// ─────────────────────────────────────────────────────────────────────────────
// UX Wording ClickUp-like pour les niveaux de permission
export const PERMISSION_LABELS: Record<PermissionLevel, { label: string; description: string }> = {
  full: { label: 'Accès complet', description: 'Peut tout modifier, gérer les membres et supprimer' },
  edit: { label: 'Peut modifier', description: 'Peut créer et modifier les tâches' },
  comment: { label: 'Peut commenter', description: 'Peut lire et commenter uniquement' },
  view: { label: 'Lecture seule', description: 'Peut uniquement consulter' },
};

// ─────────────────────────────────────────────────────────────────────────────
// IMPLICIT ADMINS (Org-level Owners/Admins with implicit full access)
// These are NOT listed in project_permissions but have full access by bypass
// Uses RPC get_org_admins for security (SECURITY INVOKER) and performance (single query)
// ─────────────────────────────────────────────────────────────────────────────
export interface ImplicitAdminInfo {
  org_member_id: string;
  user_id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  role_code: 'owner' | 'admin';
}

// ─────────────────────────────────────────────────────────────────────────────
// Module-level TTL cache for get_org_admins (5 minutes).
// Cache the RAW RPC result per orgId. Filtering by explicitMemberIds happens
// after the cache lookup so per-project filters stay correct.
// ─────────────────────────────────────────────────────────────────────────────
type OrgAdminsCacheEntry = {
  data: Array<{
    org_member_id: string;
    user_id: string;
    full_name: string | null;
    email: string;
    avatar_url: string | null;
    role_code: string;
  }>;
  expiresAt: number;
};
const ORG_ADMINS_TTL_MS = 5 * 60 * 1000;
const orgAdminsCache = new Map<string, OrgAdminsCacheEntry>();
const orgAdminsInflight = new Map<string, Promise<OrgAdminsCacheEntry['data']>>();

export function invalidateOrgAdminsCache(orgId?: string) {
  if (orgId) {
    orgAdminsCache.delete(orgId);
  } else {
    orgAdminsCache.clear();
  }
}

async function getOrgAdminsCached(orgId: string): Promise<OrgAdminsCacheEntry['data']> {
  const now = Date.now();
  const cached = orgAdminsCache.get(orgId);
  if (cached && cached.expiresAt > now) return cached.data;

  const inflight = orgAdminsInflight.get(orgId);
  if (inflight) return inflight;

  const promise = (async () => {
    const { data, error } = await supabase.rpc('get_org_admins', { _org_id: orgId });
    if (error) throw error;
    const rows = (data || []) as OrgAdminsCacheEntry['data'];
    orgAdminsCache.set(orgId, { data: rows, expiresAt: Date.now() + ORG_ADMINS_TTL_MS });
    return rows;
  })();
  orgAdminsInflight.set(orgId, promise);
  try {
    return await promise;
  } finally {
    orgAdminsInflight.delete(orgId);
  }
}

/**
 * Fetch organization owners and admins who have implicit full access to all projects.
 * Uses RPC for security (org-scoped) and performance (single query).
 * Excludes members who already have explicit project/space permissions.
 */
export async function fetchImplicitAdmins(
  orgId: string,
  explicitMemberIds: string[]
): Promise<ImplicitAdminInfo[]> {
  // Try cached RPC first
  try {
    const data = await getOrgAdminsCached(orgId);
    return data
      .filter((admin: any) => !explicitMemberIds.includes(admin.org_member_id))
      .map((admin: any) => ({
        org_member_id: admin.org_member_id,
        user_id: admin.user_id,
        full_name: admin.full_name,
        email: admin.email,
        avatar_url: admin.avatar_url,
        role_code: admin.role_code as 'owner' | 'admin',
      }));
  } catch (error: any) {
    // Fallback: direct query for org owners/admins
    console.warn('get_org_admins RPC failed, using fallback:', error?.message);
  }
  try {
    // Get roles with code owner/admin for this org
    const { data: roles } = await supabase
      .from('core_roles')
      .select('id, code')
      .eq('org_id', orgId)
      .in('code', ['owner', 'admin']);

    if (!roles || roles.length === 0) return [];

    const roleIds = roles.map(r => r.id);
    const roleMap = new Map(roles.map(r => [r.id, r.code]));

    // Get member_roles for these roles
    const { data: memberRoles } = await supabase
      .from('core_member_roles')
      .select('member_id, role_id')
      .in('role_id', roleIds);

    if (!memberRoles || memberRoles.length === 0) return [];

    const memberIds = [...new Set(memberRoles.map(mr => mr.member_id))];

    // Get members
    const { data: members } = await supabase
      .from('core_organization_members')
      .select('id, user_id, status')
      .in('id', memberIds)
      .eq('status', 'active');

    if (!members || members.length === 0) return [];

    const userIds = members.map(m => m.user_id);
    const { data: profiles } = await supabase
      .from('core_profiles')
      .select('id, full_name, email, avatar_url')
      .in('id', userIds);

    const profileMap = new Map((profiles || []).map(p => [p.id, p]));

    const admins: ImplicitAdminInfo[] = [];
    for (const member of members) {
      if (explicitMemberIds.includes(member.id)) continue;
      const profile = profileMap.get(member.user_id);
      const memberRole = memberRoles.find(mr => mr.member_id === member.id);
      const roleCode = memberRole ? roleMap.get(memberRole.role_id) : null;
      if (roleCode === 'owner' || roleCode === 'admin') {
        admins.push({
          org_member_id: member.id,
          user_id: member.user_id,
          full_name: profile?.full_name || null,
          email: profile?.email || '',
          avatar_url: profile?.avatar_url || null,
          role_code: roleCode as 'owner' | 'admin',
        });
      }
    }
    return admins;
  } catch (fallbackErr) {
    console.error('Fallback fetchImplicitAdmins failed:', fallbackErr);
    return [];
  }
}
